import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useToast } from '../../../shared/hooks/useToast';
import { visitorService } from '../../../shared/services/visitors/visitorService';
import { eventService } from '../../../shared/services/tenant/eventService';

interface DynamicField {
  id: string;
  label: string;
  field_type: 'text' | 'yes_no' | 'date' | 'number' | 'email' | 'phone' | 'select';
  required: boolean;
  options?: string[];
}

interface DynamicAnswer {
  question: string;
  answer: string;
  dynamic_field_id?: string;
}

interface EditDynamicAnswersModalProps {
  open: boolean;
  onClose: () => void;
  participantId: string;
  eventId: string;
  currentAnswers?: DynamicAnswer[];
  onSuccess?: () => void;
}

export function EditDynamicAnswersModal({
  open,
  onClose,
  participantId,
  eventId,
  currentAnswers = [],
  onSuccess,
}: EditDynamicAnswersModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [dynamicFields, setDynamicFields] = useState<DynamicField[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Buscar campos dinâmicos do evento
  useEffect(() => {
    if (open && eventId) {
      loadDynamicFields();
    }
  }, [open, eventId]);

  const loadDynamicFields = async () => {
    setLoadingFields(true);
    try {
      const event = await eventService.getById(eventId);
      
      if (event?.dynamicFields) {
        setDynamicFields(event.dynamicFields);

        // Preencher respostas existentes
        const existingAnswers: Record<string, string> = {};
        
        event.dynamicFields.forEach((field: DynamicField) => {
          // Procurar resposta existente por label (já que question === label)
          const existingAnswer = currentAnswers.find(
            (ans) => ans.question === field.label
          );
          
          if (existingAnswer) {
            existingAnswers[field.id] = existingAnswer.answer;
          } else {
            existingAnswers[field.id] = '';
          }
        });

        setAnswers(existingAnswers);
      }
    } catch (error) {
      console.error('Erro ao carregar campos dinâmicos:', error);
      showToast('Erro ao carregar campos do evento', 'error');
    } finally {
      setLoadingFields(false);
    }
  };

  const handleAnswerChange = (fieldId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Montar array de respostas apenas com campos preenchidos
      const dynamicAnswers = Object.entries(answers)
        .filter(([_, answer]) => answer.trim() !== '') // Só envia respostas não vazias
        .map(([fieldId, answer]) => ({
          dynamic_field_id: fieldId,
          answer,
        }));

      if (dynamicAnswers.length === 0) {
        showToast('Preencha pelo menos uma resposta', 'warning');
        return;
      }

      await visitorService.updateDynamicAnswers(participantId, dynamicAnswers);

      showToast('Respostas atualizadas com sucesso!', 'success');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Erro ao atualizar respostas:', error);
      showToast(
        error?.response?.data?.message || 'Erro ao atualizar respostas',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFieldInput = (field: DynamicField) => {
    const value = answers[field.id] || '';

    switch (field.field_type) {
      case 'yes_no':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value === 'Sim'}
                onChange={(e) =>
                  handleAnswerChange(field.id, e.target.checked ? 'Sim' : 'Não')
                }
              />
            }
            label={value === 'Sim' ? 'Sim' : 'Não'}
          />
        );

      case 'select':
        return (
          <TextField
            select
            fullWidth
            value={value}
            onChange={(e) => handleAnswerChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
            size="small"
          >
            <MenuItem value="">
              <em>Selecione...</em>
            </MenuItem>
            {field.options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            value={value}
            onChange={(e) => handleAnswerChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
            size="small"
          />
        );

      case 'email':
        return (
          <TextField
            fullWidth
            type="email"
            value={value}
            onChange={(e) => handleAnswerChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
            size="small"
          />
        );

      case 'phone':
        return (
          <TextField
            fullWidth
            type="tel"
            value={value}
            onChange={(e) => handleAnswerChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
            size="small"
          />
        );

      case 'text':
      default:
        return (
          <TextField
            fullWidth
            multiline
            rows={2}
            value={value}
            onChange={(e) => handleAnswerChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
            size="small"
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Editar Respostas Adicionais
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Adicione ou atualize as respostas do participante
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loadingFields ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : dynamicFields.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Este evento não possui campos dinâmicos configurados
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {dynamicFields.map((field) => (
              <Box key={field.id}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {field.label}
                  {field.required && (
                    <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                  )}
                </Typography>
                {renderFieldInput(field)}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || loadingFields || dynamicFields.length === 0}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

